import React, { FormEvent, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrivalGlyph, GuestsGlyph } from './RequestIcons';
import { ServiceIcon, TransportIcon } from './MenuGlyphs';
import { TransitGlyphCycle } from './TransitGlyphCycle';
import { DestinationField } from './DestinationField';
import { legalLinks } from '../data/contact';
import { destinations } from '../data/content';
import { contact } from '../data/contact';
import { needOptions, parties, transportOptions, transportUndecided } from '../data/requestOptions';
import { MultiSelectField } from './MultiSelectField';
import { InlineSelect } from './InlineSelect';
import { DateRangeField } from './DateRangeField';
import { ConsentCheckbox } from './ConsentCheckbox';
import { DialCodeSelect } from './DialCodeSelect';
import { useDialCode } from '../hooks/useDialCode';
import { useRequestIntro } from '../hooks/useRequestIntro';
import { format } from 'date-fns';
import { saveSubmission, SUBMISSION_ERROR } from '../utils/submissions';
const whereOptions = [...destinations.map(d => d.city), 'Worldwide'];
// The intro settles on one of these, with the request most asked for there
const favourites = ['Dubai', 'Monaco', 'Mallorca'];
const favouriteConcierge: Record<string, string> = {
  Dubai: 'Penthouse suite',
  Monaco: 'Yacht with crew',
  Mallorca: 'Staffed villa'
};
// Private client line used for WhatsApp hand-off (international format, digits only)
const WHATSAPP_NUMBER = contact.whatsappNumber;
const ease = [0.22, 1, 0.36, 1] as const;
type Status = 'idle' | 'sending' | 'sent';
type ConciergeRequestBarProps = {
  idPrefix?: string;
  className?: string;
  /** Which part of the site this bar sits in — saved with each submission */
  placement?: 'hero' | 'membership_page';
};
export function ConciergeRequestBar({
  idPrefix = 'req',
  className = 'border-b border-line px-6 py-12 md:px-16 md:py-16',
  placement = 'hero'
}: ConciergeRequestBarProps) {
  const [party, setParty] = useState(parties[1]);
  const [where, setWhere] = useState('');
  const [transport, setTransport] = useState('');
  const [needs, setNeeds] = useState<string[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [months, setMonths] = useState<string[]>([]);
  // WhatsApp is the main route; a phone number is only asked for when chosen
  const [usePhone, setUsePhone] = useState(false);
  const [dialCode, setDialCode] = useDialCode();
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);

  // A popular request, suggested on load by a human-paced typewriter
  const [suggestion] = useState(() => {
    const where = favourites[Math.floor(Math.random() * favourites.length)];
    const pool = whereOptions.filter(c => c !== where && c !== 'Worldwide').sort(() => Math.random() - 0.5);
    const next = new Date();
    next.setMonth(next.getMonth() + 2, 1);
    const month = format(next, 'MMM yyyy');
    return {
      decoys: pool.slice(0, 2),
      where,
      concierge: favouriteConcierge[where],
      month,
      dates: month
    };
  });
  // Each suggested field is committed as soon as it is typed, and stays until changed.
  // A field passes to the visitor only once they actually choose or type something in
  // it — taps, hovers, scroll-touches or simply opening a list never interrupt it.
  const {
    state: intro,
    skip: takeOver
  } = useRequestIntro({
    ...suggestion,
    onCommit: field => {
      if (field === 'where') setWhere(suggestion.where);
      if (field === 'concierge') setNeeds([suggestion.concierge]);
      if (field === 'dates') setMonths([suggestion.month]);
    }
  });
  // Typing a character into a field (e.g. searching a city) also counts as choosing
  const ownField = (field: 'where' | 'concierge' | 'dates') => ({
    onKeyDownCapture: (e: React.KeyboardEvent) => {
      if (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete') takeOver(field);
    }
  });
  const dates = months.length ? `${months.join(', ')} (flexible)` : startDate ? `${startDate}${endDate ? ` – ${endDate}` : ''}` : '';
  const openWhatsApp = () => {
    const parts = [`Party: ${party}`, where && `Destination: ${where}`, needs.length > 0 && `Needs: ${needs.join(', ')}`, transport && `Arrival: ${transport}`, dates && `Dates: ${dates}`].filter(Boolean);
    const text = `Good day, Superluxury. I would like to arrange the following:\n${parts.join('\n')}`;
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };
  const requestDetails = () => ({
    party,
    destination: where,
    needs,
    arrival: transport,
    dates
  });
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!usePhone) {
      openWhatsApp();
      // Logged in the background — never holds up the WhatsApp hand-off
      void saveSubmission({
        source: `${placement}_request_whatsapp`,
        details: requestDetails()
      });
      return;
    }
    const missingWhere = !where;
    const badPhone = phone.replace(/\D/g, '').length < 6;
    setError(missingWhere ? 'Choose a destination above — or select Worldwide.' : '');
    setPhoneError(badPhone ? 'Please leave a number we can reach you on.' : '');
    setConsentError(!consent);
    if (missingWhere || badPhone || !consent) return;
    setStatus('sending');
    const ok = await saveSubmission({
      source: `${placement}_request_phone`,
      phone: `${dialCode} ${phone}`,
      details: {
        ...requestDetails(),
        consent: true
      }
    });
    if (ok) {
      setStatus('sent');
    } else {
      setError(SUBMISSION_ERROR);
      setStatus('idle');
    }
  };
  const reset = () => {
    setStatus('idle');
    setWhere('');
    setTransport('');
    setNeeds([]);
    setStartDate('');
    setEndDate('');
    setMonths([]);
    setPhone('');
  };
  const summary = [where, transport, needs.join(', '), dates, party].filter(Boolean).join(' · ');
  return <section aria-label="Private request" className={`bg-paper text-ink ${className}`} data-id="element-214" data-mp="gdo1r9lao7qi">
      <AnimatePresence mode="wait" initial={false} data-id="element-215" data-mp="lchakopmzakc">
        {status === 'sent' ? <motion.div key="sent" initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease
      }} className="flex flex-col gap-5 py-6 md:flex-row md:items-center md:justify-between" role="status" data-id="element-216" data-mp="7horxheg9e9j">
            <div data-id="element-217" data-mp="3kozz9rh3b9xu">
              <p className="text-[22px] font-normal" data-id="element-218" data-mp="3kqksndro4mu4">Received</p>
              <p className="mt-2 max-w-[880px] text-[17px] font-light text-ink/75" data-id="element-219" data-mp="3oqv9xmkwuzoa">
                {summary} — your concierge will call {dialCode} {phone} from a private line within the hour.
              </p>
            </div>
            <button type="button" onClick={reset} className="self-start whitespace-nowrap border border-ink px-6 py-3 text-[16px] font-medium transition-colors duration-300 hover:bg-ink hover:text-paper md:self-auto" data-id="element-220" data-mp="314rmot1vmq13">
              Begin another
            </button>
          </motion.div> : <motion.form key="form" onSubmit={submit} noValidate initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease
      }} aria-label="Concierge request" data-id="element-221" data-mp="3k2lreuvz2fdo">
            {/* Options row: guests · arrival — small bold selectors */}
            <div className="flex flex-wrap items-center gap-x-10 gap-y-3" data-id="element-222" data-mp="bkoaw96v4p1m">
              <InlineSelect icon={<GuestsGlyph className="h-[19px] w-[19px]" data-id="element-224" data-mp="1e054215lmrpg" />} label="Party" value={party} options={parties} onChange={setParty} data-id="element-223" data-mp="6p8yzmsf137u" />
              <InlineSelect icon={<ArrivalGlyph className="h-[19px] w-[19px]" data-id="element-226" data-mp="1n1uw997b1uoa" />} label="Need a ride?" value={transport} placeholder="Need a ride?" heading="We’ll get you there" intro="Transport concierge — jet, car, yacht or helicopter, arranged door to door to your destination." renderIcon={o => <TransportIcon option={o} data-id="element-227" data-mp="14abu14l3uknb" />} iconWidth="w-9" dividerBefore={transportUndecided} options={transportOptions} onChange={setTransport} data-id="element-225" data-mp="1n0tfqp7isfz6" />
            </div>

            {/* One line, as on Lufthansa: destination → services · dates · send */}
            <div className="mt-6 grid grid-cols-1 items-end gap-6 md:grid-cols-3 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_minmax(0,1fr)_210px] lg:gap-x-5 xl:gap-x-6" data-id="element-228" data-mp="gjzbkzs8h5ih">
              <div {...ownField('where')} data-id="element-229" data-mp="42et4y19b0c9">
              <DestinationField id={`${idPrefix}-where`} value={where} options={whereOptions} typed={intro.typed.where} typing={intro.active === 'where'} onChange={v => {
              takeOver('where');
              setWhere(v);
              if (error) setError('');
            }} invalid={Boolean(error)} describedBy={error ? `${idPrefix}-error` : undefined} data-id="element-230" data-mp="43gepwfwf588" />
              </div>
              {/* Leads on to the next field, as Lufthansa's arrow between From and To */}
              <span className="hidden pb-3.5 lg:flex" aria-hidden="true" data-id="element-231" data-mp="37h12ng19rgnp">
                <TransitGlyphCycle className="h-7 w-14 text-ink rtl:-scale-x-100" data-id="element-232" data-mp="32izissw0rdb6" />
              </span>
              <div {...ownField('concierge')} data-id="element-233" data-mp="2yiz3l613iy32">
              <MultiSelectField id={`${idPrefix}-what`} label="Looking for" smallLabel endTick placeholder="Concierge" typed={intro.typed.concierge} typing={intro.active === 'concierge'} options={needOptions} renderIcon={o => <ServiceIcon service={o} data-id="element-235" data-mp="1wsjin660fhuy" />} values={needs} onChange={v => {
              takeOver('concierge');
              setNeeds(v);
            }} data-id="element-234" data-mp="1rz6u0w1bzb27" />
              </div>
              <div {...ownField('dates')} data-id="element-236" data-mp="1i1aii34hee3k">
              <DateRangeField id={`${idPrefix}-dates`} startDate={startDate} endDate={endDate} onStartChange={v => {
              takeOver('dates');
              setStartDate(v);
            }} onEndChange={v => {
              takeOver('dates');
              setEndDate(v);
            }} placeholder="Departure — Return" endPlaceholder="Return" bold months={months} onMonthsChange={v => {
              takeOver('dates');
              setMonths(v);
            }} typed={intro.typed.dates} typing={intro.active === 'dates'} data-id="element-237" data-mp="1i33ttvq1hmhw" />
              </div>
              <div className="md:col-span-3 lg:col-span-1" data-id="element-238" data-mp="3fpmugdkeuph9">
                <AnimatePresence mode="wait" initial={false} data-id="element-239" data-mp="3koqzcs0ay11s">
                  {usePhone ? <motion.button key="apply" type="submit" disabled={status === 'sending'} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.18,
                ease
              }} className="flex h-[54px] w-full items-center justify-center whitespace-nowrap bg-ink px-6 text-[16px] font-medium text-paper transition-opacity duration-150 hover:opacity-85 disabled:opacity-60" data-id="element-240" data-mp="35zuvl75a3a6z">
                      {status === 'sending' ? 'Sending…' : 'Request your concierge'}
                    </motion.button> : <motion.div key="whatsapp" initial={{
                opacity: 0
              }} animate={intro.highlight ? {
                opacity: 1,
                scale: [1, 1.05, 1.05, 1],
                boxShadow: ['0 0 0 0 rgba(37,211,102,0)', '0 10px 30px -6px rgba(37,211,102,0.55)', '0 10px 30px -6px rgba(37,211,102,0.55)', '0 0 0 0 rgba(37,211,102,0)']
              } : {
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0
              }} transition={intro.highlight ? {
                duration: 2.4,
                times: [0, 0.25, 0.75, 1],
                ease: 'easeInOut'
              } : {
                duration: 0.18,
                ease
              }} className="relative p-[2px]" data-id="element-241" data-mp="350kthnuh0vd">
                      {/* A band of light runs around the outline at the CTA moment */}
                      {intro.highlight && <span className="cta-shine" aria-hidden="true" data-id="element-242" data-mp="1w1up6l78wdf4" />}
                      <button type="submit" className="cta-flash flex h-[50px] w-full items-center justify-center gap-2.5 whitespace-nowrap bg-[#25D366] px-5 text-[16px] font-semibold text-white transition-colors duration-200 hover:bg-[#1EBE5A]" data-id="element-243" data-mp="203zj7l1lvc2a">
                        <WhatsAppGlyph className="relative h-[22px] w-[22px] shrink-0" data-id="element-244" data-mp="1cdvgrh05licv" />
                        <span className="relative" data-id="element-245" data-mp="1h4vcwk1g7web">Arrange now</span>
                      </button>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>

            {/* Below: data agreement (left) · alternative route (right) */}
            <div className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between lg:gap-8" data-id="element-246" data-mp="5k7hbl5m827s">
              {/* Notice only; the agreement box appears when a phone number is left */}
              {usePhone ? <ConsentCheckbox compact id={`${idPrefix}-consent`} checked={consent} onChange={v => {
            setConsent(v);
            if (v) setConsentError(false);
          }} error={consentError} data-id="element-247" data-mp="fb2e3jghmk7h" /> : <p className="text-[13px] font-light leading-[20px] text-ink/70" data-id="element-248" data-mp="y8v4jm9xd1nz">
                  Under NDA, always. Your details are used per our{' '}
                  <Link to={legalLinks.privacyPolicy} className="text-ink underline underline-offset-2 transition-opacity duration-150 hover:opacity-70" data-id="element-249" data-mp="tb3bgoxjn9i7">
                    data policy
                  </Link>
                  .
                </p>}
              <button type="button" onClick={() => {
            setUsePhone(u => !u);
            setPhoneError('');
            setError('');
          }} aria-expanded={usePhone} aria-controls={`${idPrefix}-phone-row`} className="shrink-0 self-start whitespace-nowrap text-[13px] font-light text-ink/70 underline underline-offset-2 transition-colors duration-150 hover:text-ink lg:self-auto lg:text-right" data-id="element-250" data-mp="1aiktp69q4y2k">
                {usePhone ? 'Use WhatsApp' : 'No WhatsApp? Leave number instead'}
              </button>
            </div>

            {/* Phone number — only when chosen */}
            <AnimatePresence initial={false} data-id="element-251" data-mp="msqguopen9ib">
              {usePhone && <motion.div key="phone" id={`${idPrefix}-phone-row`}
          // Clipped only while it opens/closes, so the country-code list can drop down past it
          initial={{
            height: 0,
            opacity: 0,
            overflow: 'hidden'
          }} animate={{
            height: 'auto',
            opacity: 1,
            transitionEnd: {
              overflow: 'visible'
            }
          }} exit={{
            height: 0,
            opacity: 0,
            overflow: 'hidden'
          }} transition={{
            duration: 0.25,
            ease
          }} className="relative z-20" data-id="element-252" data-mp="2c8pvumfpft5v">
                  <div className="pt-6 lg:grid lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_minmax(0,1fr)_210px] lg:gap-x-5 xl:gap-x-6" data-id="element-253" data-mp="20v7relrfu4y9">
                    <div className="lg:col-span-2 lg:col-start-4" data-id="element-254" data-mp="2th8za12m7klo">
                    <label htmlFor={`${idPrefix}-phone`} className="sr-only" data-id="element-255" data-mp="2tk4vnkyte180">
                      Where may we reach you?
                    </label>
                    <div className="flex items-end gap-3 border-b-[1.5px] border-ink" data-id="element-256" data-mp="2jqxvceixlbes">
                      <DialCodeSelect id={`${idPrefix}-code`} value={dialCode} onChange={setDialCode} data-id="element-257" data-mp="3h7ms3tt0xnhb" />
                      <span className="mb-3.5 h-5 w-px bg-ink/25" aria-hidden="true" data-id="element-258" data-mp="3cgelt6mzqi8d" />
                      <input id={`${idPrefix}-phone`} type="tel" inputMode="tel" autoComplete="tel-national" autoFocus value={phone} onChange={e => {
                    setPhone(e.target.value);
                    if (phoneError) setPhoneError('');
                  }} placeholder="Your phone number" aria-invalid={Boolean(phoneError)} aria-describedby={phoneError ? `${idPrefix}-phone-error` : undefined} className="min-w-0 flex-1 bg-transparent pb-3 pt-1.5 text-[18px] placeholder:font-light placeholder:text-ink/60 focus:outline-none" data-id="element-259" data-mp="3kpskgx7qxk3k" />
                    </div>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>

            {(error || phoneError) && <div role="alert" className="mt-3 text-[14px] text-ink lg:text-right" data-id="element-260" data-mp="1ghqjkksdci1b">
                {error && <p id={`${idPrefix}-error`} data-id="element-261" data-mp="1bjp03yr16gpb">{error}</p>}
                {phoneError && <p id={`${idPrefix}-phone-error`} data-id="element-262" data-mp="1pk8855vhxmrh">{phoneError}</p>}
              </div>}
          </motion.form>}
      </AnimatePresence>
    </section>;
}

// Official WhatsApp mark (white), for the green WhatsApp button
function WhatsAppGlyph({
  className = ''
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true" data-id="element-263" data-mp="sv0grh874jfn">
      <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.6.13-.14.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.21 3.07.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.7.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35M12.05 21.5h-.01a9.43 9.43 0 0 1-4.81-1.32l-.35-.2-3.57.93.96-3.48-.23-.36a9.4 9.4 0 0 1-1.44-5.02c0-5.2 4.24-9.44 9.46-9.44 2.52 0 4.9.99 6.68 2.77a9.37 9.37 0 0 1 2.76 6.68c0 5.21-4.24 9.44-9.45 9.44m8.05-17.5A11.3 11.3 0 0 0 12.05.67C5.78.67.67 5.77.67 12.04c0 2 .52 3.96 1.52 5.68L.57 23.33l5.75-1.5a11.36 11.36 0 0 0 5.73 1.46h.01c6.27 0 11.38-5.1 11.38-11.37 0-3.04-1.18-5.9-3.34-8.04" data-id="element-264" data-mp="16m5jwtarvtuq" />
    </svg>;
}